import { ViewBuilderIsViewSymbol, ViewBuilderTargetSymbol, ViewSymbol, } from "./constants.js";
import { cloneView, createEmptyView, getMutableViewDeclarationFromViewBuilder, isElementBuilderChildren, isElementDeclarationKeyInput, isElementDeclarationPropsInput, isElementDeclarationRefInput, isViewDeclaration, } from "./utils/mod.js";
var SpecialProperties;
(function (SpecialProperties) {
    SpecialProperties["Children"] = "$children";
    SpecialProperties["ChildrenShorthand"] = "$";
    SpecialProperties["Key"] = "$key";
    SpecialProperties["Props"] = "$props";
    SpecialProperties["Ref"] = "$ref";
    /**
     * A blocked property to prevent the proxy from being
     * resolved like a promise.
     *
     * @remarks
     *
     * This is used to prevent the proxy being recognized
     * as a `PromiseLike` object, which would cause the `then`
     * method to be called, and the resulting "promise" is
     * never resolved.
     */
    SpecialProperties["Then"] = "then";
})(SpecialProperties || (SpecialProperties = {}));
function createViewBuilderFactory() {
    function viewBuilderFactory(input) {
        let target;
        if (isViewDeclaration(input)) {
            target = cloneView(input);
        }
        else {
            target = createEmptyView(input);
        }
        const viewBuilder = new Proxy({}, {
            get: (_fakeTarget, propertyName, _receiver) => {
                if (propertyName === ViewBuilderTargetSymbol) {
                    return target;
                }
                if (propertyName === ViewBuilderIsViewSymbol) {
                    return true;
                }
                if (propertyName === SpecialProperties.Then) {
                    // This is a blocked property to prevent the proxy from being
                    // resolved like a promise.
                    return undefined;
                }
                return (...args) => {
                    const [value, ..._rest] = args;
                    const rest = _rest;
                    /**
                     * This is the next element builder that will be returned.
                     */
                    // TODO: Refactor to use `handoffToElementBuilder` instead.
                    const nextViewBuilder = viewBuilderFactory(target);
                    /**
                     * This is the next element declaration from the next element builder.
                     * This object can be safely mutated with the requested changes,
                     * before returning the next element builder.
                     */
                    // TODO: Refactor to use `handoffToElementBuilder` instead.
                    const nextView = getMutableViewDeclarationFromViewBuilder(nextViewBuilder);
                    if (propertyName === SpecialProperties.Children ||
                        propertyName === SpecialProperties.ChildrenShorthand) {
                        const firstChild = value == null ? [] : [value];
                        const children = [...firstChild, ...rest];
                        if (!isElementBuilderChildren(children)) {
                            throw new Error("Invalid child type");
                        }
                        nextView.children = children;
                    }
                    else if (propertyName === SpecialProperties.Ref) {
                        if (!isElementDeclarationRefInput(value)) {
                            throw new Error("Invalid ref type");
                        }
                        nextView.ref = value;
                    }
                    else if (propertyName === SpecialProperties.Key) {
                        if (!isElementDeclarationKeyInput(value)) {
                            throw new Error(`Invalid key type: ${typeof value}`);
                        }
                        nextView.key = value;
                    }
                    else if (propertyName === SpecialProperties.Props) {
                        if (!isElementDeclarationPropsInput(value)) {
                            throw new Error(`Invalid props value type: ${typeof value}`);
                        }
                        Object.assign(nextView.props, value);
                    }
                    else {
                        nextView.props[propertyName] = value;
                    }
                    return nextViewBuilder;
                };
            },
        });
        // Type assertion is needed because of the Proxy
        return viewBuilder;
    }
    return viewBuilderFactory;
}
function createViewBuilder(render) {
    return createViewBuilderFactory()(render);
}
/**
 * Defines a view with the given render function.
 */
export function defineView(render, _options) {
    const view = () => {
        return createViewBuilder(
        // Type assertion is needed because of enforcing the partial object type.
        render);
    };
    view[ViewSymbol] = true;
    // Type assertion is needed, because of the the use of `EnforcePartialProps<P>`.
    return view;
}
export function isView(value) {
    return (typeof value === "function" &&
        ViewSymbol in value &&
        value[ViewSymbol] === true);
}
